import React, { createContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import HTTPSCalls from "../Services/HttpCalls";

export const UserContext = createContext();

const UserProvider = ({ children }) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const reactDomainUrl = process.env.REACT_APP_DOMAIN_URL;

  // Hydrate user from sessionStorage so PrivateRoute renders immediately on reload
  const [user, setUser] = useState(() => {
    try {
      const cached = sessionStorage.getItem("cachedUser");
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [deliveryIds, setDeliveryIds] = useState([]);
  const [currencies, setCurrencies] = useState(() => {
    try {
      const cached = sessionStorage.getItem("cachedCurrencies");
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  // If we have a cached user + token, skip the loading gate so PrivateRoute renders instantly
  const [isLoading, setIsLoading] = useState(() => {
    const token = localStorage.getItem("token");
    try {
      const cached = sessionStorage.getItem("cachedUser");
      return !(token && cached);
    } catch { return true; }
  });
  const [refres, setRefresh] = useState(false);
  const [vesselinvite, setVesselinvite] = useState(false);
  const [transferRequestId, setTransferRequestId] = useState(null);
  // Track if user was just set from login — skip /user/get only once
  const skipNextFetchRef = useRef(false);
  const currenciesFetchedRef = useRef(false);
  const subscriptionToggleFetchedRef = useRef(false);
  const [subscriptionToggle, setSubscriptionToggle] = useState(() => {
    try {
      const cached = sessionStorage.getItem("cachedSubscriptionToggle");
      return cached !== null ? JSON.parse(cached) : 1;
    } catch { return 1; }
  });

  // Persist user to sessionStorage for instant hydration on next load
  useEffect(() => {
    if (user) {
      sessionStorage.setItem("cachedUser", JSON.stringify(user));
    } else {
      sessionStorage.removeItem("cachedUser");
    }
  }, [user]);

  // Fetch currencies only once per session (they rarely change)
  useEffect(() => {
    if (currenciesFetchedRef.current) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    currenciesFetchedRef.current = true;
    const headers = { Authorization: `Bearer ${token}` };
    HTTPSCalls.GET(`${apiUrl}/user/currency`, {}, headers)
      .then((currencyRes) => {
        setCurrencies(currencyRes.data);
        sessionStorage.setItem("cachedCurrencies", JSON.stringify(currencyRes.data));
      })
      .catch((err) => console.debug("Currency fetch error:", err));
  }, [apiUrl]);

  // Fetch subscription toggle once per session
  useEffect(() => {
    if (subscriptionToggleFetchedRef.current) return;
    const cached = sessionStorage.getItem("cachedSubscriptionToggle");
    if (cached !== null) return; // already hydrated from sessionStorage

    subscriptionToggleFetchedRef.current = true;
    axios.get(`${apiUrl}/get/subscription/toggle`)
      .then((response) => {
        if (response.data && typeof response.data.subscription_toggle !== 'undefined') {
          const value = response.data.subscription_toggle;
          setSubscriptionToggle(value);
          sessionStorage.setItem("cachedSubscriptionToggle", JSON.stringify(value));
        }
      })
      .catch((err) => console.debug("Subscription toggle fetch error:", err));
  }, [apiUrl]);

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Right after login, user is already set — skip /user/get once
      if (skipNextFetchRef.current) {
        skipNextFetchRef.current = false;
        setIsLoading(false);
        return;
      }

      try {
        const headers = { Authorization: `Bearer ${token}` };
        const response = await HTTPSCalls.GET(
          `${apiUrl}${HTTPSCalls.EndPoints.GET_USER}`,
          {},
          headers
        );

        setUser(response.data.user);
        setDeliveryIds(response?.data?.deliveryRequest);
      } catch (error) {
        console.error("Error fetching data:", error);
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          sessionStorage.removeItem("cachedUser");
          sessionStorage.removeItem("cachedCurrencies");
          sessionStorage.removeItem("cachedSubscriptionToggle");
          window.location.href = `${reactDomainUrl}/login`;
        }
      }

      setIsLoading(false);
    };

    fetchUserData();
  }, [refres]);

  // Exposed helper to refresh currencies on-demand
  const refreshCurrencies = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const currencyRes = await HTTPSCalls.GET(
        `${apiUrl}/user/currency`,
        {},
        { Authorization: `Bearer ${token}` }
      );
      setCurrencies(currencyRes.data);
      sessionStorage.setItem("cachedCurrencies", JSON.stringify(currencyRes.data));
      return currencyRes.data;
    } catch (err) {
      console.error('Error refreshing currencies:', err);
      return null;
    }
  }, [apiUrl]);

  const contextValue = useMemo(() => ({
    user,
    setUser,
    isLoading,
    setRefresh,
    refres,
    deliveryIds,
    currencies,
    vesselinvite,
    setVesselinvite,
    transferRequestId,
    setTransferRequestId,
    refreshCurrencies,
    skipNextFetchRef,
    subscriptionToggle,
  }), [user, isLoading, refres, deliveryIds, currencies, vesselinvite, transferRequestId, refreshCurrencies, subscriptionToggle]);

  return (
    <UserContext.Provider
      value={contextValue}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserProvider;
